
import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import * as CookieManager from '@/lib/managers/CookieManager';
import { WordSelectionStrategyFactory } from '@/lib/wordSelection/StrategyFactory';
import { clearCachedOverlayState } from '@/components/game/managers/OverlayManager';
import { toast } from 'sonner';

// Define available game modes
export type GameMode = 'daily' | 'practice' | 'custom';

// Configuration for each game mode
export interface GameModeConfig {
  name: string;
  description: string;
  storagePrefix: string;
  isEnabled: boolean;
}

interface GameModeContextType {
  currentMode: GameMode;
  setCurrentMode: (mode: GameMode) => void;
  availableModes: Record<GameMode, GameModeConfig>;
  getModeConfig: (mode?: GameMode) => GameModeConfig;
  switchToMode: (targetMode: GameMode, preserveTargetState: boolean) => Promise<boolean>;
}

const defaultModes: Record<GameMode, GameModeConfig> = {
  daily: {
    name: 'Daily Challenge',
    description: 'Play the daily word sequence that everyone gets',
    storagePrefix: 'babblegram_daily_', // Fixed to use daily prefix
    isEnabled: true,
  },
  practice: {
    name: 'Practice Mode',
    description: 'Practice with random word sequences',
    storagePrefix: 'babblegram_practice_',
    isEnabled: true, // Enable practice mode
  },
  custom: {
    name: 'Custom Game',
    description: 'Play with custom word sequences',
    storagePrefix: 'babblegram_custom_',
    isEnabled: false, // Not implemented yet
  },
};

export const GameModeContext = createContext<GameModeContextType>({
  currentMode: 'daily',
  setCurrentMode: () => {},
  availableModes: defaultModes,
  getModeConfig: () => defaultModes.daily,
  switchToMode: async () => false,
});

export const GameModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Try to get the stored mode from localStorage
  const getStoredMode = (): GameMode => {
    try {
      if (typeof window !== 'undefined') {
        const storedMode = localStorage.getItem('babblegram_game_mode');
        if (storedMode && Object.keys(defaultModes).includes(storedMode)) {
          return storedMode as GameMode;
        }
      }
    } catch (error) {
      console.error('Error reading game mode from storage:', error);
    }
    return 'daily';
  };

  const [currentMode, setCurrentMode] = useState<GameMode>(getStoredMode);
  const [availableModes] = useState<Record<GameMode, GameModeConfig>>(defaultModes);

  // Store the selected mode in localStorage when it changes
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const previousMode = localStorage.getItem('babblegram_game_mode');
        
        if (previousMode !== currentMode) {
          console.log(`Game mode changed from ${previousMode} to ${currentMode}`, {
            timestamp: new Date().toISOString()
          });
        }
        
        localStorage.setItem('babblegram_game_mode', currentMode);
      }
    } catch (error) {
      console.error('Error saving game mode to storage:', error);
    }
  }, [currentMode]);

  const getModeConfig = useCallback((mode?: GameMode): GameModeConfig => {
    const modeToUse = mode || currentMode;
    return availableModes[modeToUse];
  }, [availableModes, currentMode]);

  /**
   * Switch to a different game mode with proper state preservation
   * @param targetMode The mode to switch to
   * @param preserveTargetState Whether to preserve any existing state in the target mode
   * @returns Promise that resolves when the mode switch is complete
   */
  const switchToMode = useCallback(async (targetMode: GameMode, preserveTargetState: boolean = true): Promise<boolean> => {
    if (currentMode === targetMode) {
      console.log(`Already in ${targetMode} mode`);
      return true;
    }

    try {
      console.log(`Switching from ${currentMode} to ${targetMode}`);
      
      // Update mode immediately for clean separation
      setCurrentMode(targetMode);
      localStorage.setItem('babblegram_game_mode', targetMode);
      
      // Mode switching should not clear any state - each mode is independent
      
      console.log(`Successfully switched to ${targetMode} mode`);
      
      // Dispatch mode switch event with debouncing to prevent rapid-fire events
      setTimeout(() => {
        const event = new CustomEvent('babblegram_mode_switched', {
          detail: { 
            previousMode: currentMode, 
            targetMode: targetMode
          }
        });
        window.dispatchEvent(event);
      }, 50);
      
      return true;
    } catch (error) {
      console.error('Error switching game mode:', error);
      return false;
    }
  }, [currentMode]);

  return (
    <GameModeContext.Provider
      value={{
        currentMode,
        setCurrentMode,
        availableModes,
        getModeConfig,
        switchToMode,
      }}
    >
      {children}
    </GameModeContext.Provider>
  );
};

export const useGameMode = () => useContext(GameModeContext);
